import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * A web page in the sealed frame (gyro-core sealedFrame.ts; docs/DESIGN-html-preview-panel.md): the iframe on the frame page's
 * own origin, sandboxed with scripts alone, handed the page once over the handshake (sealedFrameSession.ts). Reusable: the
 * side panel's HTML preview is its first user. Mount it with a fresh `key` to run the page again from the start.
 *
 * What the person may read here, besides the page: "Starting the preview…" until the frame page answers; the host's
 * `unavailableText` when it never does (a host app whose policy or proxy refuses the frame page) or when the host's own
 * frame-src refuses it outright; and a note when the frame itself navigated, after which it is started again (a frame that
 * keeps doing so is stopped). The page's own notes (it tried to open another address) are the frame page's to show.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { SEALED_FRAME_SANDBOX } from '@exante/gyro-core/sealed-frame';
import { SealedFrameSession, frameOrigin, refusedFrame } from './sealedFrameSession.js';
/** How long the frame page has to say it is listening before the host's "not available" text shows. */
export const SEALED_FRAME_READY_TIMEOUT_MS = 10_000;
/** The frame started again more than this often within RESTART_WINDOW_MS is a loop, and is stopped. */
const MAX_RESTARTS = 3;
const RESTART_WINDOW_MS = 10_000;
export function SealedFrame({ html, frameUrl, title, unavailableText }) {
    const frameRef = useRef(null);
    const sessionRef = useRef(null);
    const restartsRef = useRef([]);
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState('starting');
    const [note, setNote] = useState('');
    // A session per frame page load, in place before the iframe can load (a message is a task; this runs before any).
    useLayoutEffect(() => {
        const session = new SealedFrameSession(html, () => frameRef.current?.contentWindow);
        sessionRef.current = session;
        setState((current) => (current === 'stopped' ? current : 'starting'));
        const origin = frameOrigin(frameUrl, window.location.href);
        const onMessage = (event) => {
            if (session.message(event))
                setState('running');
        };
        const onViolation = (event) => {
            if (!session.rendered && refusedFrame(event, origin))
                setState('unavailable');
        };
        window.addEventListener('message', onMessage);
        document.addEventListener('securitypolicyviolation', onViolation);
        const timer = setTimeout(() => {
            if (!session.rendered)
                setState((current) => (current === 'starting' ? 'unavailable' : current));
        }, SEALED_FRAME_READY_TIMEOUT_MS);
        return () => {
            window.removeEventListener('message', onMessage);
            document.removeEventListener('securitypolicyviolation', onViolation);
            clearTimeout(timer);
            if (sessionRef.current === session)
                sessionRef.current = null;
        };
    }, [html, frameUrl, attempt]);
    const onLoad = () => {
        const session = sessionRef.current;
        if (!session || session.load() === 'first')
            return;
        // The frame itself navigated (only its page could have done it): start it again, unless that keeps happening.
        const now = Date.now();
        const recent = restartsRef.current.filter((at) => now - at < RESTART_WINDOW_MS);
        recent.push(now);
        restartsRef.current = recent;
        if (recent.length > MAX_RESTARTS) {
            setState('stopped');
            setNote('The page kept trying to leave the preview, so Gyro stopped it.');
            return;
        }
        setNote('The page tried to leave the preview, so Gyro started it again.');
        setAttempt((value) => value + 1);
    };
    return _jsxs("div", { className: "gyro-sealed-frame", "data-state": state, children: [state !== 'stopped' && _jsx("iframe", { ref: frameRef, src: frameUrl, sandbox: SEALED_FRAME_SANDBOX, referrerPolicy: "no-referrer", title: title, onLoad: onLoad, hidden: state === 'unavailable' }, attempt), state === 'starting' && _jsx("p", { className: "gyro-sealed-frame-status", role: "status", children: "Starting the preview\u2026" }), state === 'unavailable' && _jsx("p", { className: "gyro-sealed-frame-status", role: "alert", children: unavailableText }), note && _jsx("p", { className: "gyro-sealed-frame-note", role: "status", children: note })] });
}
//# sourceMappingURL=SealedFrame.js.map